import { useEffect, useState } from 'react';
import { DEFAULT_FITTER_TRADES, type FitterTradeOption } from '../data/tradeJobs';
import { items, text, type Localized } from '../shared/primitives';

/**
 * Pieces shared by the trade and fitter blocks. Kept beside them rather than
 * in `shared/` because only they use them.
 */

/** An owner-authored trade in a block's `trades` prop. */
export interface TradeOptionProp {
  id?: string;
  label?: Localized;
}

/** The block's trade list, or the default list when none is authored. Entries need both an id and a label. */
export function tradeOptions(value: unknown): FitterTradeOption[] {
  const authored = items<TradeOptionProp>(value)
    .map((item) => ({ id: (item.id ?? '').trim(), label: text(item.label) }))
    .filter((item) => item.id && item.label);
  const unique = authored.filter((item, index) => authored.findIndex((other) => other.id === item.id) === index);
  return unique.length ? unique : [...DEFAULT_FITTER_TRADES];
}

/**
 * `signInHref` with `next=` set to this page, the storefront's sign-in return
 * convention (see commerce's `signInHrefFor`). The page path is read after
 * mount, so the server render and the first client render match.
 */
export function useSignInHref(signInHref: string | undefined): string {
  const base = signInHref?.trim() || '/sign-in';
  const [here, setHere] = useState<string | null>(null);
  useEffect(() => {
    setHere(`${window.location.pathname}${window.location.search}`);
  }, []);
  if (!here) return base;
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}next=${encodeURIComponent(here)}`;
}

/**
 * Sends a visitor who is not signed in to sign in and back here, as the account
 * pages do (`SpatialKitchenMyDesigns`). Called only on a settled "not signed in",
 * never while a check is still running.
 */
export function sendToSignIn(signInHref: string | undefined): void {
  const base = signInHref?.trim() || '/sign-in';
  const here = `${window.location.pathname}${window.location.search}`;
  window.location.replace(`${base}${base.includes('?') ? '&' : '?'}next=${encodeURIComponent(here)}`);
}

/** The page holding the fitter application (commerce's `InstallerApplicationForm`). */
export const FITTER_APPLICATION_HREF = '/fitter-application';

/** What stops an approved fitter quoting, in words, by the backend's blocker name. */
export const FITTER_BLOCKER_TEXT: Readonly<Record<string, string>> = {
  AGREEMENT_OUT_OF_DATE: 'The fitter agreement has changed. Read and sign the new one.',
  NOT_INSURED: 'Your insurance is not current. Upload a renewed certificate.',
  SUSPENDED: 'Your fitter account is suspended. Please contact us.',
  CERTIFICATES_OUTSTANDING: 'We still need your insurance certificate before we can review your application. Upload it from your account page.',
};

export function blockerText(blocker: string): string {
  return FITTER_BLOCKER_TEXT[blocker] ?? 'Something on your fitter account needs attention. Please contact us.';
}

/** An owner-authored section id: letters, digits, `-` and `_`, starting with a letter; otherwise none. */
export function anchorId(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z][\w-]{0,63}$/.test(value) ? value : undefined;
}

/** An error's message for a shopper, or the fallback when the error carries none. */
export function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
